<template>
  <div
    class="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/50 border border-gray-100 dark:border-gray-700/50 hover:shadow-md transition-shadow"
  >
    <!-- Action Header -->
    <div
      @click="$emit('toggle-expand', action.id)"
      class="px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors rounded-lg"
      :class="{ 'bg-green-50/50 dark:bg-green-900/20': action.status === 'completed' }"
    >
      <!-- Top row: Type badge, status, due date (right aligned) -->
      <div class="flex items-center gap-2 mb-1.5">
        <span
          class="inline-flex px-2 py-0.5 text-xs font-medium rounded"
          :class="getActionTypeBadgeClass(action.action_type_code)"
        >
          {{ action.action_type_name || 'Actie' }}
        </span>
        <span
          class="px-2 py-0.5 text-xs font-medium rounded-full"
          :class="getStatusBadgeClass(action.status)"
        >
          {{ getStatusLabel(action.status) }}
        </span>
        <div v-if="editingDeadline" class="ml-auto w-44" @click.stop>
          <DatePickerLite
            :model-value="action.due_date"
            auto-open
            @update:model-value="onDeadlineSelected"
            @close="editingDeadline = false"
          />
        </div>
        <button
          v-else-if="action.due_date && canEditDeadline"
          type="button"
          :disabled="updating"
          class="flex items-center gap-1.5 ml-auto px-2.5 py-1 rounded-lg cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-blue-400 transition-shadow disabled:opacity-60 disabled:cursor-not-allowed"
          :class="getDueDateBadgeClass(action)"
          :title="'Deadline aanpassen'"
          @click.stop="editingDeadline = true"
        >
          <ClockIcon class="h-4 w-4" />
          <span class="text-sm font-semibold">{{ formatDueDate(action.due_date) }}</span>
          <PencilSquareIcon class="h-3.5 w-3.5 opacity-70" />
        </button>
        <div
          v-else-if="action.due_date"
          class="flex items-center gap-1.5 ml-auto px-2.5 py-1 rounded-lg"
          :class="getDueDateBadgeClass(action)"
        >
          <ClockIcon class="h-4 w-4" />
          <span class="text-sm font-semibold">{{ formatDueDate(action.due_date) }}</span>
        </div>
      </div>

      <!-- Subject -->
      <h3
        class="font-medium text-gray-900 dark:text-white"
        :class="{
          'line-through text-gray-500 dark:text-gray-400': action.status === 'completed'
        }"
      >
        {{ action.prospect_subject }}
      </h3>

      <!-- Organization name -->
      <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
        {{ action.organization_name }}
      </p>

      <!-- Actiehouder(s) -->
      <div
        v-if="action.assigned_to_name"
        class="flex items-center gap-1 mt-2 text-xs text-gray-500 dark:text-gray-400"
      >
        <UserIcon class="h-3.5 w-3.5" /><span>{{ action.assigned_to_name }}</span>
      </div>
      <div
        v-if="action.secondary_assigned_to_name"
        class="flex items-center gap-1 mt-1 text-xs text-gray-500 dark:text-gray-400"
      >
        <UserIcon class="h-3.5 w-3.5" />
        <span>PL: {{ action.secondary_assigned_to_name }}</span>
      </div>

      <!-- Expand indicator -->
      <div class="flex justify-center mt-2">
        <ChevronDownIcon
          class="h-4 w-4 text-gray-400 transition-transform duration-200"
          :class="{ 'rotate-180': expanded }"
        />
      </div>
    </div>

    <!-- Expanded Content -->
    <div
      v-show="expanded"
      class="border-t border-gray-100 dark:border-gray-700 px-4 py-4 bg-gray-50/50 dark:bg-gray-800/50"
    >
      <!-- Description -->
      <div v-if="action.description" class="mb-4">
        <h4
          class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1"
        >
          Beschrijving
        </h4>
        <p class="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
          {{ action.description }}
        </p>
      </div>

      <!-- Sub-actions list -->
      <div v-if="action.items && action.items.length > 0" class="mb-4">
        <h4
          class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2"
        >
          Sub-acties
        </h4>
        <div class="space-y-1">
          <div
            v-for="item in action.items"
            :key="item.id"
            class="flex items-baseline gap-2 p-2 rounded-lg"
          >
            <span class="text-sm text-gray-400 dark:text-gray-500">•</span>
            <span class="text-sm text-gray-700 dark:text-gray-300">
              {{ item.display_name }}
            </span>
          </div>
        </div>
      </div>

      <!-- Action buttons -->
      <DoenActionButtons
        :action="action"
        :updating="updating"
        :is-admin="isAdmin"
        @deal="$emit('deal', $event)"
        @no-deal="$emit('no-deal', $event)"
        @confirm-create-project="$emit('confirm-create-project', $event)"
        @pl-no-match="$emit('pl-no-match', $event)"
        @complete="$emit('complete', $event)"
        @delete="$emit('delete', $event)"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { ChevronDownIcon, ClockIcon, PencilSquareIcon, UserIcon } from '@heroicons/vue/24/outline'
import { getStatusLabel, getStatusBadgeClass, getActionTypeBadgeClass } from '@/constants/actions'
import DoenActionButtons from '@/components/doen/DoenActionButtons.vue'
import DatePickerLite from '@/components/common/inputs/DatePickerLite.vue'

const props = defineProps({
  action: { type: Object, required: true },
  expanded: { type: Boolean, default: false },
  updating: { type: Boolean, default: false },
  isAdmin: { type: Boolean, default: false }
})

const emit = defineEmits([
  'toggle-expand',
  'deal',
  'no-deal',
  'confirm-create-project',
  'pl-no-match',
  'complete',
  'delete',
  'update-deadline'
])

const editingDeadline = ref(false)

const canEditDeadline = computed(
  () => props.isAdmin && (props.action.status === 'open' || props.action.status === 'in_progress')
)

const onDeadlineSelected = newDate => {
  editingDeadline.value = false
  if (!newDate || newDate === props.action.due_date) return
  emit('update-deadline', { id: props.action.id, due_date: newDate })
}

// Format due date
const formatDueDate = dateString => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)

  const dueDate = new Date(date)
  dueDate.setHours(0, 0, 0, 0)

  if (dueDate.getTime() === today.getTime()) {
    return 'Vandaag'
  }
  if (dueDate.getTime() === tomorrow.getTime()) {
    return 'Morgen'
  }

  return date.toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

// Check if action is overdue
const isOverdue = action => {
  if (!action.due_date || action.status === 'completed') return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const dueDate = new Date(action.due_date)
  dueDate.setHours(0, 0, 0, 0)
  return dueDate < today
}

// Get due date badge class with background colors based on urgency
const getDueDateBadgeClass = action => {
  if (action.status === 'completed') {
    return 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
  }
  if (isOverdue(action)) {
    return 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
  }
  // Check if due today
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const dueDate = new Date(action.due_date)
  dueDate.setHours(0, 0, 0, 0)
  if (dueDate.getTime() === today.getTime()) {
    return 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300'
  }
  // Check if due tomorrow
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)
  if (dueDate.getTime() === tomorrow.getTime()) {
    return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300'
  }
  // Default - upcoming
  return 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
}
</script>
